import { useCallback, useEffect, useState } from 'react';
import type { DependencyStatus, HealthResponse } from '@makemypartner/shared';
import { API_URL, fetchHealth } from '@/lib/api/health';
import { Card } from '@/components/ui';

const TONE: Record<DependencyStatus['status'], string> = {
  up: 'var(--online)',
  down: 'var(--danger)',
  not_configured: 'var(--busy)',
};

const LABEL: Record<DependencyStatus['status'], string> = {
  up: 'up',
  down: 'down',
  not_configured: 'not configured',
};

function DependencyRow({ dep }: { dep: DependencyStatus }) {
  const tone = TONE[dep.status];
  return (
    <li className="flex items-center gap-3.5 border-b border-[var(--line-soft)] px-5 py-3.5 transition-colors duration-150 last:border-0 hover:bg-[var(--surface-2)]">
      <span className="size-2 shrink-0 rounded-full" style={{ background: tone }} aria-hidden />
      <span className="w-20 shrink-0 font-medium">{dep.name}</span>
      <span
        className="rounded-full px-2 py-0.5 font-[family-name:var(--font-mono)] text-[11px] font-semibold tracking-wide uppercase"
        style={{ color: tone, background: `color-mix(in srgb, ${tone} 12%, transparent)` }}
      >
        {LABEL[dep.status]}
      </span>
      <span className="ml-auto truncate text-right text-sm text-[var(--muted)] tabular-nums">
        {dep.latencyMs !== undefined ? `${dep.latencyMs}ms` : dep.detail}
      </span>
    </li>
  );
}

export default function Status() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      setHealth(await fetchHealth(signal));
      setError(null);
    } catch (err) {
      if (signal?.aborted) return;
      setHealth(null);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    const id = setInterval(() => void load(controller.signal), 10_000);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [load]);

  return (
    <div className="animate-rise mx-auto max-w-2xl">
      <p className="eyebrow">Internal</p>
      <h1 className="mt-2 text-3xl font-semibold">System status</h1>
      <p className="mt-3 text-[var(--ink-2)]">
        Developer view. Confirms the web app, the API and the worker are talking to each other.
      </p>

      <Card raised className="mt-7 overflow-hidden">
        <div
          className="flex items-center justify-between gap-4 px-5 py-3.5"
          style={{ background: 'var(--surface-2)' }}
        >
          <h2 className="font-semibold">Dependencies</h2>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 font-[family-name:var(--font-mono)] text-[11px] font-semibold tracking-wide uppercase transition-all duration-150 hover:-translate-y-px hover:border-[var(--marigold)]"
          >
            {loading ? 'Checking…' : 'Refresh'}
          </button>
        </div>

        {error ? (
          <div className="px-5 py-5">
            <div
              className="rounded-xl border-l-[3px] px-4 py-3.5 text-sm"
              style={{ borderColor: 'var(--danger)', background: 'var(--danger-soft)' }}
            >
              <p className="font-medium">{error}</p>
              <p className="mt-1.5 text-[var(--ink-2)]">
                Start it with <code className="font-[family-name:var(--font-mono)]">pnpm dev</code>,
                then check{' '}
                <code className="font-[family-name:var(--font-mono)]">{API_URL}/health</code>.
              </p>
            </div>
          </div>
        ) : health ? (
          <>
            <ul>
              {health.dependencies.map((dep) => (
                <DependencyRow key={dep.name} dep={dep} />
              ))}
            </ul>
            <p
              className="px-5 py-3 font-[family-name:var(--font-mono)] text-xs text-[var(--muted)] tabular-nums"
              style={{ background: 'var(--surface-2)' }}
            >
              api v{health.version} · up {health.uptimeSec}s · checked{' '}
              {new Date(health.checkedAt).toLocaleTimeString()}
            </p>
          </>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-[var(--muted)]">Contacting the API…</p>
        )}
      </Card>

      <p className="mt-6 text-sm text-[var(--muted)]">
        A <strong className="text-[var(--ink-2)]">not configured</strong> dependency is expected
        until Phase 1. A <strong className="text-[var(--ink-2)]">down</strong> worker means Redis is
        not running —{' '}
        <code className="font-[family-name:var(--font-mono)] text-xs">docker compose up -d</code>.
      </p>
    </div>
  );
}
